import { keepPreviousData, useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { t, type TKey } from "@/lib/i18n";
import { proto3 } from "@bufbuild/protobuf";
import { createClient } from "@/lib/connect";
import { AuditService } from "@gen/tracker/v1/audit_connect";
import { AuditEvent, SearchAuditEventsRequest } from "@gen/tracker/v1/audit_pb";
import { DateRange, Pagination, ResourceKind } from "@gen/tracker/v1/common_pb";

const client = createClient(AuditService);

export const auditKeys = {
  all: ["audit-events"] as const,
  search: (params: SearchAuditEventsParams) => [...auditKeys.all, "search", params] as const,
};

export interface SearchAuditEventsParams {
  /** ISO YYYY-MM-DD bounds, both inclusive. */
  range: { from: string; to: string };
  actorId?: string;
  resourceKind?: ResourceKind;
  resourceId?: string;
  action?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

/** Pure SearchAuditEventsParams → pb request projection (unit-testable). */
export function buildSearchAuditEventsRequest(
  params: SearchAuditEventsParams,
): SearchAuditEventsRequest {
  return new SearchAuditEventsRequest({
    pagination: new Pagination({
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
    }),
    range: new DateRange({ from: params.range.from, to: params.range.to }),
    actorId: params.actorId ?? "",
    resourceKind: params.resourceKind ?? ResourceKind.UNSPECIFIED,
    resourceId: params.resourceId ?? "",
    action: params.action ?? "",
    search: params.search ?? "",
  });
}

export function useSearchAuditEvents(
  params: SearchAuditEventsParams,
  options?: Partial<UseQueryOptions<{ items: AuditEvent[]; total: number }>>,
) {
  return useQuery({
    queryKey: auditKeys.search(params),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await client.searchAuditEvents(buildSearchAuditEventsRequest(params));
      return {
        items: res.items,
        total: res.pagination?.total ?? res.items.length,
      };
    },
    ...options,
  });
}

/**
 * actionTone — maps the dotted action suffix ("campaign.update" → "update")
 * to status-pill tone classes. Unknown suffixes fall back to muted.
 */
export function actionTone(action: string): string {
  const suffix = action.split(".").pop()?.toLowerCase() ?? "";
  switch (suffix) {
    case "create":
    case "activate":
      return "bg-ok/15 text-ok";
    case "update":
    case "duplicate":
      return "bg-info/15 text-info";
    case "delete":
      return "bg-destructive/15 text-destructive";
    case "pause":
      return "bg-warn/15 text-warn";
    case "archive":
    default:
      return "bg-muted text-muted-foreground";
  }
}

export interface ParsedDiff {
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

/**
 * parseDiff — safe-parses the before/after JSON snapshots from an audit row.
 * Returns null when neither side parses to an object (nothing to diff);
 * otherwise each side is the parsed object or null.
 */
export function parseDiff(beforeJson: string, afterJson: string): ParsedDiff | null {
  const before = safeParseObject(beforeJson);
  const after = safeParseObject(afterJson);
  if (!before && !after) return null;
  return { before, after };
}

function safeParseObject(raw: string): Record<string, unknown> | null {
  if (!raw || !raw.trim()) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

const KIND_LABEL: Partial<Record<ResourceKind, TKey>> = {
  [ResourceKind.WORKSPACE]: "audit.kind.workspace",
  [ResourceKind.TRAFFIC_SOURCE]: "audit.kind.trafficSource",
  [ResourceKind.AFFILIATE_NETWORK]: "audit.kind.network",
  [ResourceKind.OFFER]: "audit.kind.offer",
  [ResourceKind.LANDER]: "audit.kind.lander",
  [ResourceKind.CAMPAIGN]: "audit.kind.campaign",
  [ResourceKind.FLOW]: "audit.kind.flow",
  [ResourceKind.FLOW_TEMPLATE]: "audit.kind.flowTemplate",
  [ResourceKind.TRACKING_DOMAIN]: "audit.kind.domain",
  [ResourceKind.INTEGRATION]: "audit.kind.integration",
  [ResourceKind.TAG_MANAGER_SNIPPET]: "audit.kind.snippet",
  [ResourceKind.ALERT_RULE]: "audit.kind.alertRule",
  [ResourceKind.RULE]: "audit.kind.rule",
  [ResourceKind.CONTENT_SET]: "audit.kind.contentSet",
};

/** The kind of object a journal row is about, in words. */
export function resourceKindLabel(kind: ResourceKind): string {
  const info = proto3.getEnumType(ResourceKind).findNumber(kind);
  if (!info || kind === ResourceKind.UNSPECIFIED) return "—";
  const key = KIND_LABEL[kind];
  return key ? t(key) : prettifyEnumName(info.localName);
}

const VERB_LABEL: Record<string, TKey> = {
  create: "audit.verb.create",
  update: "audit.verb.update",
  pause: "audit.verb.pause",
  activate: "audit.verb.activate",
  archive: "audit.verb.archive",
  duplicate: "audit.verb.duplicate",
  delete: "audit.verb.delete",
};

/** "campaign.update" → «изменение»; an unknown verb stays as it is written. */
export function actionLabel(action: string): string {
  const suffix = action.split(".").pop()?.toLowerCase() ?? "";
  const key = VERB_LABEL[suffix];
  return key ? t(key) : action.replace(/[._]/g, " ");
}

/**
 * resourceKindOptions — every concrete ResourceKind as {value,label}, derived
 * from the generated enum metadata so the list never drifts from the proto.
 */
export function resourceKindOptions(): { value: string; label: string }[] {
  return proto3
    .getEnumType(ResourceKind)
    .values.filter((v) => v.no !== ResourceKind.UNSPECIFIED)
    .map((v) => ({ value: String(v.no), label: resourceKindLabel(v.no as ResourceKind) }));
}

function prettifyEnumName(localName: string): string {
  return localName
    .split("_")
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
}
